import { IApplication } from "shared/model/IApplication";
import { IGooglePlace } from "shared/model/ICoworkGeoLocation";
import {
  IMemberBotPageParams,
  MemberBotType
} from "shared/pages/MembersBotPage";
import { CreateFollowupType } from "shared/functions/createFollowupJob";
import FirestoreService from "shared/services/firebase/FirestoreService";

import { IState } from "..";
import { PlaidAuthenticationData } from "../../../components/PlaidAuthenticator/PlaidAuthenticator";
import FirebaseDatabaseService from "../../../services/firebase/database/FirebaseDatabseService";
import FirestoreObserver from "../../../services/firebase/database/observers/FirestoreObserver";
import FirestoreObserverFactory from "../../../services/firebase/database/observers/FirestoreObserverFactory";
import FirebaseFunctionsService from "../../../services/firebase/functions/FirebaseFunctionsService";
import RavenService from "../../../services/Logging/RavenService";
import { Dispatch, getState } from "../../store/store";
import { selectMemberBotType } from "./MemberChatBotType";
import { createLandbotProps } from "./MemberChatLandbotProps";
import MembersChatSelectors from "./MembersChatSelectors";

let applicationObserver: FirestoreObserver<IApplication> | undefined;

export const initMemberSession = async (
  params: IMemberBotPageParams,
  state: IState
) => {
  const { id, cowork } = params;
  if (id) {
    await fetchApplication(id, cowork);
    setMembersBotData(id, params, getState());
  } else {
    const generatedId = FirebaseDatabaseService.createUnassingnedApplicationId();
    Dispatch.membersChat.setApplicationId({ id: generatedId });
    setMembersBotData(generatedId, params, getState());
  }
};

const setMembersBotData = (
  applicationId: string,
  params: IMemberBotPageParams,
  state: IState
) => {
  const botType = params.beta
    ? MemberBotType.Beta
    : selectMemberBotType(state.membersChat);
  const application = MembersChatSelectors.application(state.membersChat);
  const coworkName = MembersChatSelectors.coworkName(state.membersChat);
  const props = createLandbotProps(applicationId!, application, coworkName);
  Dispatch.membersChat.setBotData({ type: botType, props });
};

export const stopRefreshingApplication = (params: {}, state: IState) => {
  if (applicationObserver) {
    applicationObserver.unsubscribe();
    applicationObserver = undefined;
  }
};

export const refreshApplication = async (params: {}, state: IState) => {
  const applicationId = MembersChatSelectors.applicationId(state.membersChat);
  const coworkName = MembersChatSelectors.coworkName(state.membersChat);
  const location = MembersChatSelectors.selectedPlaceCoworkLocation(
    state.membersChat
  );
  const hasCowork = coworkName || location;
  if (applicationId && hasCowork) {
    const finalCoworkName = coworkName || location!.coworkName;
    applicationObserver = FirestoreObserverFactory.applicationObserver(
      finalCoworkName,
      applicationId
    );

    applicationObserver.subscribe(application => {
      const applicationCoworkName = application
        ? application.coworkName
        : undefined;
      Dispatch.membersChat.applicationFetchSuccess({
        application,
        coworkName: coworkName || applicationCoworkName,
        applicationId
      });
    });
  } else {
    console.log(
      `Failed refreshing application. ${coworkName} ${location &&
        location.coworkName}`
    );
  }
};

const fetchApplication = async (applicationId: string, coworkName?: string) => {
  Dispatch.membersChat.fetchingApplication();

  try {
    const application = await FirestoreService.getApplication({
      coworkName,
      applicationId
    });

    await Dispatch.membersChat.applicationFetchSuccess({
      application,
      coworkName: application.coworkName,
      applicationId
    });
  } catch (error) {
    console.error(error);
    RavenService.captureMessage(
      `Failed loading members bot with id=${applicationId}. Error = ${JSON.stringify(
        error
      )}`
    );
    Dispatch.membersChat.applicationFetchError({
      error: `Failed fetching application ${JSON.stringify(error)}`
    });
  }
};

export const updateApplicationPlaidAuth = async (
  params: { data: PlaidAuthenticationData },
  state: IState
) => {
  const applicationId = MembersChatSelectors.applicationId(state.membersChat);
  const coworkName = MembersChatSelectors.coworkName(state.membersChat);
  const {
    data: { publicKey, institutionId, institutionName }
  } = params;

  try {
    if (!applicationId) {
      throw new Error(`Can't update auth with no application id.`);
    }

    const reponse = await FirebaseFunctionsService.updateApplicationPlaidAuth({
      applicationId,
      publicKey,
      institutionId,
      institutionName,
      coworkName
    });

    if (!reponse.ok) {
      throw new Error(JSON.stringify(reponse));
    }
  } catch (error) {
    console.error(error);
    RavenService.captureMessage(
      `Failed updating plaid auth. Public token was ${publicKey} ${error}`
    );
  }
};

export const updateSelectedPlace = async (
  params: { place: IGooglePlace | string | undefined },
  state: IState
) => {
  try {
    const applicationId = MembersChatSelectors.applicationId(
      state.membersChat
    )!;
    const { place } = params;
    if (typeof place === "object") {
      try {
        const location = await FirebaseDatabaseService.getCoworkGeoLocation(
          place.id
        );

        await FirebaseDatabaseService.setUnassignedApplicationMetadata({
          uid: applicationId,
          location
        });

        Dispatch.membersChat.setCoworkLocation({
          location
        });

        console.log(
          `Updated application ${applicationId} with location ${JSON.stringify(
            location
          )}`
        );
      } catch (error) {
        console.log(`No location for ${place.id} ${error}`);
      }
    } else {
      console.log(`Did not select place from list`);
    }
  } catch (error) {
    console.error(error);
  }
};

export const createFollowupJob = async (
  params: { type: CreateFollowupType },
  state: IState
) => {
  try {
    const appId = MembersChatSelectors.applicationId(state.membersChat);
    const coworkName = MembersChatSelectors.coworkName(state.membersChat);

    await FirebaseFunctionsService.createFollowupJob({
      type: params.type,
      id: appId!,
      cowork_name: coworkName
    });
  } catch (error) {
    RavenService.captureException(error, "Failed creating follow up job");
  }
};



// WEBPACK FOOTER //
// ./src/state/modules/membersChat/MembersChatEffetcs.ts